export const LANDING_COPY_LIMITS = Object.freeze({
  EYEBROW: 40,
  HERO_TITLE: 50,
  SECTION_TITLE: 60,
  SECTION_SUPPORT: 100,
  CTA: 30,
  NAV_ITEM: 25,
  CARD_TITLE: 30,
  CARD_DESCRIPTION: 100,
  BUSINESS_SHORT_DESCRIPTION: 200,
  ABOUT_LONG_DESCRIPTION: 600,
  CUSTOM_SECTION_LONG_TEXT: 800,
  FOOTER_SHORT_TEXT: 100,
  IMAGE_ALT: 60,
});

const EXACT_COPY_ROLES = Object.freeze({
  "hero.eyebrow": "EYEBROW",
  "hero.title": "HERO_TITLE",
  "hero.subtitle": "SECTION_SUPPORT",
  "hero.primaryCtaLabel": "CTA",
  "hero.secondaryCtaLabel": "CTA",

  "navigation.homeLabel": "NAV_ITEM",
  "navigation.aboutLabel": "NAV_ITEM",
  "navigation.highlightsLabel": "NAV_ITEM",
  "navigation.productsLabel": "NAV_ITEM",
  "navigation.contactLabel": "NAV_ITEM",
  "navigation.loginLabel": "NAV_ITEM",
  "navigation.accountLabel": "NAV_ITEM",
  "navigation.logoutLabel": "NAV_ITEM",
  "navigation.cartLabel": "NAV_ITEM",
  "navigation.allProductsLabel": "CTA",
  "navigation.megaMenuEyebrow": "EYEBROW",
  "navigation.megaMenuTitle": "SECTION_TITLE",
  "navigation.megaMenuDescription": "SECTION_SUPPORT",
  "navigation.megaMenuCategoriesSubtitle": "SECTION_SUPPORT",
  "navigation.megaMenuSortLabel": "SECTION_SUPPORT",
  "navigation.megaMenuLatestLabel": "SECTION_SUPPORT",
  "navigation.socialTitle": "SECTION_TITLE",

  "catalog.spotlight.eyebrow": "EYEBROW",
  "catalog.spotlight.title": "SECTION_TITLE",
  "catalog.spotlight.description": "SECTION_SUPPORT",
  "catalog.spotlight.ctaLabel": "CTA",
  "catalog.spotlight.logoAlt": "IMAGE_ALT",

  "catalog.featured.eyebrow": "EYEBROW",
  "catalog.featured.title": "SECTION_TITLE",
  "catalog.featured.subtitle": "SECTION_SUPPORT",
  "catalog.featured.ctaLabel": "CTA",
  "catalog.brands.eyebrow": "EYEBROW",
  "catalog.brands.title": "SECTION_TITLE",
  "catalog.brands.subtitle": "SECTION_SUPPORT",
  "catalog.brands.ctaLabel": "CTA",
  "catalog.categories.eyebrow": "EYEBROW",
  "catalog.categories.title": "SECTION_TITLE",
  "catalog.categories.subtitle": "SECTION_SUPPORT",
  "catalog.categories.ctaLabel": "CTA",
  "catalog.cards.ctaLabel": "CTA",
  "catalog.cards.serviceCtaLabel": "CTA",

  "about.eyebrow": "EYEBROW",
  "about.title": "SECTION_TITLE",
  "about.description": "ABOUT_LONG_DESCRIPTION",
  "about.ctaLabel": "CTA",
  "about.imageAlt": "IMAGE_ALT",

  "highlightsIntro.eyebrow": "EYEBROW",
  "highlightsIntro.title": "SECTION_TITLE",
  "highlightsIntro.subtitle": "SECTION_SUPPORT",

  "contact.title": "SECTION_TITLE",
  "contact.subtitle": "SECTION_SUPPORT",
  "contact.detailsCtaLabel": "CTA",
  "contact.whatsappCtaLabel": "CTA",
  "contact.mapTitle": "SECTION_TITLE",

  "footer.tagline": "FOOTER_SHORT_TEXT",
  "footer.copyright": "FOOTER_SHORT_TEXT",
  "footer.navigationTitle": "SECTION_TITLE",
  "footer.contactTitle": "SECTION_TITLE",
  "footer.homeLabel": "NAV_ITEM",
  "footer.productsLabel": "NAV_ITEM",
  "footer.contactLabel": "NAV_ITEM",
  "business.shortDescription": "BUSINESS_SHORT_DESCRIPTION",
});

function normalizedPath(path) {
  return String(path || "").trim().replace(/\.\d+(?=\.|$)/g, ".*");
}

export function landingCopyConstraint(path) {
  const candidate = String(path || "").trim();
  if (!candidate) return null;
  const exactRole = EXACT_COPY_ROLES[candidate];
  if (exactRole) return { role: exactRole, max: LANDING_COPY_LIMITS[exactRole] };

  const normalized = normalizedPath(candidate);
  let role = "";
  if (/^hero\.rotatingPhrases\.\*$/.test(normalized)) role = "HERO_TITLE";
  else if (/^hero\.slides\.\*\.title$/.test(normalized)) role = "HERO_TITLE";
  else if (/^hero\.slides\.\*\.subtitle$/.test(normalized)) role = "SECTION_SUPPORT";
  else if (/^hero\.slides\.\*\.alt$/.test(normalized)) role = "IMAGE_ALT";
  else if (/^navigation\.buttons\.\*\.label$/.test(normalized)) role = "NAV_ITEM";
  else if (/^catalog\.brands\.items\.\*\.name$/.test(normalized)) role = "CARD_TITLE";
  else if (/^catalog\.brands\.items\.\*\.alt$/.test(normalized)) role = "IMAGE_ALT";
  else if (/^highlights\.\*\.title$/.test(normalized)) role = "CARD_TITLE";
  else if (/^highlights\.\*\.description$/.test(normalized)) role = "CARD_DESCRIPTION";
  else if (/^highlights\.\*\.imageAlt$/.test(normalized)) role = "IMAGE_ALT";
  else if (/^layout\.segments\.\*\.data\.custom\.eyebrow$/.test(normalized)) role = "EYEBROW";
  else if (/^layout\.segments\.\*\.data\.custom\.title$/.test(normalized)) role = "SECTION_TITLE";
  else if (/^layout\.segments\.\*\.data\.custom\.description$/.test(normalized)) role = "CUSTOM_SECTION_LONG_TEXT";
  else if (/^layout\.segments\.\*\.data\.custom\.ctaLabel$/.test(normalized)) role = "CTA";
  else if (/^layout\.segments\.\*\.data\.custom\.imageAlt$/.test(normalized)) role = "IMAGE_ALT";

  return role ? { role, max: LANDING_COPY_LIMITS[role] } : null;
}

function getAtPath(source, path, fallback = undefined) {
  const value = String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, rawKey) => {
      const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
      return current?.[key];
    }, source);
  return value === undefined ? fallback : value;
}

function sameText(value, other) {
  return String(value ?? "") === String(other ?? "");
}

export function landingCopyLength(value) {
  return Array.from(String(value ?? "")).length;
}

function findById(items, id) {
  if (!id || !Array.isArray(items)) return null;
  return items.find((item) => String(item?.id || "") === String(id)) || null;
}

function historicalByTextOccurrence(currentItems, baselineItems, index, valueForItem) {
  const currentList = Array.isArray(currentItems) ? currentItems : [];
  const baselineList = Array.isArray(baselineItems) ? baselineItems : [];
  const currentValue = valueForItem(currentList[index]);
  let occurrence = 0;
  for (let itemIndex = 0; itemIndex <= index; itemIndex += 1) {
    if (sameText(valueForItem(currentList[itemIndex]), currentValue)) occurrence += 1;
  }
  if (!occurrence) return undefined;
  const matches = baselineList.filter((item) => sameText(valueForItem(item), currentValue));
  return matches[occurrence - 1];
}

export function historicalLandingCopyValue(form, baselineForm, path) {
  const candidatePath = String(path || "").trim();
  if (!candidatePath) return "";
  const normalized = normalizedPath(candidatePath);
  const parts = candidatePath.split(".");

  if (/^navigation\.buttons\.\*\.label$/.test(normalized)) {
    const index = Number(parts[2]);
    const currentItem = form?.navigation?.buttons?.[index];
    const baselineItem = findById(baselineForm?.navigation?.buttons, currentItem?.id);
    return baselineItem ? baselineItem.label : baselineForm?.navigation?.buttons?.[index]?.label;
  }

  if (/^highlights\.\*\.(title|description|imageAlt)$/.test(normalized)) {
    const index = Number(parts[1]);
    const key = parts[2];
    const currentItem = form?.highlights?.[index];
    const baselineItem = findById(baselineForm?.highlights, currentItem?.id);
    return baselineItem ? baselineItem?.[key] : baselineForm?.highlights?.[index]?.[key];
  }

  if (/^layout\.segments\.\*\.data\.custom\./.test(normalized)) {
    const index = Number(parts[2]);
    const key = parts[5];
    const currentSegment = form?.layout?.segments?.[index];
    const baselineSegment = findById(baselineForm?.layout?.segments, currentSegment?.id);
    return baselineSegment?.data?.custom?.[key] ?? baselineForm?.layout?.segments?.[index]?.data?.custom?.[key];
  }

  if (/^hero\.slides\.\*\.(title|subtitle|alt)$/.test(normalized)) {
    const index = Number(parts[2]);
    const key = parts[3];
    const currentItem = form?.hero?.slides?.[index] || {};
    const baselineItems = Array.isArray(baselineForm?.hero?.slides) ? baselineForm.hero.slides : [];
    const byUrl = currentItem?.url
      ? baselineItems.find((item) => String(item?.url || "") === String(currentItem.url))
      : null;
    if (byUrl) return byUrl?.[key];
    const byValue = historicalByTextOccurrence(form?.hero?.slides, baselineItems, index, (item) => item?.[key]);
    return byValue?.[key] ?? baselineItems[index]?.[key];
  }

  if (/^catalog\.brands\.items\.\*\.(name|alt)$/.test(normalized)) {
    const index = Number(parts[3]);
    const key = parts[4];
    const currentItem = form?.catalog?.brands?.items?.[index] || {};
    const baselineItems = Array.isArray(baselineForm?.catalog?.brands?.items) ? baselineForm.catalog.brands.items : [];
    const byStableData = baselineItems.find((item) => {
      if (currentItem?.logoUrl && String(item?.logoUrl || "") === String(currentItem.logoUrl)) return true;
      if (currentItem?.query && String(item?.query || "") === String(currentItem.query)) return true;
      return false;
    });
    if (byStableData) return byStableData?.[key];
    const byValue = historicalByTextOccurrence(form?.catalog?.brands?.items, baselineItems, index, (item) => item?.[key]);
    return byValue?.[key] ?? baselineItems[index]?.[key];
  }

  if (/^hero\.rotatingPhrases\.\*$/.test(normalized)) {
    const index = Number(parts[2]);
    const baselineItems = Array.isArray(baselineForm?.hero?.rotatingPhrases) ? baselineForm.hero.rotatingPhrases : [];
    const same = historicalByTextOccurrence(form?.hero?.rotatingPhrases, baselineItems, index, (value) => value);
    return same ?? baselineItems[index];
  }

  return getAtPath(baselineForm, candidatePath, "");
}

function pushEntry(entries, form, path, label = path) {
  const constraint = landingCopyConstraint(path);
  if (!constraint) return;
  entries.push({
    path,
    label,
    role: constraint.role,
    max: constraint.max,
    value: String(getAtPath(form, path, "") ?? ""),
  });
}

export function collectLandingCopyEntries(form = {}) {
  const entries = [];
  Object.keys(EXACT_COPY_ROLES).forEach((path) => pushEntry(entries, form, path));

  (Array.isArray(form?.hero?.rotatingPhrases) ? form.hero.rotatingPhrases : []).forEach((_, index) => {
    pushEntry(entries, form, `hero.rotatingPhrases.${index}`, `Portada · frase rotativa ${index + 1}`);
  });
  (Array.isArray(form?.hero?.slides) ? form.hero.slides : []).forEach((_, index) => {
    pushEntry(entries, form, `hero.slides.${index}.title`, `Portada · slide ${index + 1} · título`);
    pushEntry(entries, form, `hero.slides.${index}.subtitle`, `Portada · slide ${index + 1} · subtítulo`);
    pushEntry(entries, form, `hero.slides.${index}.alt`, `Portada · slide ${index + 1} · texto alternativo`);
  });
  (Array.isArray(form?.navigation?.buttons) ? form.navigation.buttons : []).forEach((_, index) => {
    pushEntry(entries, form, `navigation.buttons.${index}.label`, `Navbar · botón ${index + 1}`);
  });
  (Array.isArray(form?.catalog?.brands?.items) ? form.catalog.brands.items : []).forEach((_, index) => {
    pushEntry(entries, form, `catalog.brands.items.${index}.name`, `Marcas · tarjeta ${index + 1} · nombre`);
    pushEntry(entries, form, `catalog.brands.items.${index}.alt`, `Marcas · tarjeta ${index + 1} · texto alternativo`);
  });
  (Array.isArray(form?.highlights) ? form.highlights : []).forEach((_, index) => {
    pushEntry(entries, form, `highlights.${index}.title`, `Diferenciales · tarjeta ${index + 1} · título`);
    pushEntry(entries, form, `highlights.${index}.description`, `Diferenciales · tarjeta ${index + 1} · descripción`);
    pushEntry(entries, form, `highlights.${index}.imageAlt`, `Diferenciales · tarjeta ${index + 1} · texto alternativo`);
  });
  (Array.isArray(form?.layout?.segments) ? form.layout.segments : []).forEach((segment, index) => {
    if (String(segment?.type || "") !== "custom") return;
    ["eyebrow", "title", "description", "ctaLabel", "imageAlt"].forEach((key) => {
      pushEntry(entries, form, `layout.segments.${index}.data.custom.${key}`, `Sección libre ${index + 1} · ${key}`);
    });
  });

  return entries;
}

export function landingCopyFieldState({ path, value, form, baselineForm }) {
  const constraint = landingCopyConstraint(path);
  if (!constraint) return null;
  const text = String(value ?? "");
  const length = landingCopyLength(text);
  const baselineValue = historicalLandingCopyValue(form, baselineForm, path);
  const legacyUnchanged = length > constraint.max && sameText(text, baselineValue);
  const exceeded = length > constraint.max;
  const invalid = exceeded && !legacyUnchanged;
  return {
    ...constraint,
    length,
    exceeded,
    invalid,
    legacyUnchanged,
    overBy: Math.max(0, length - constraint.max),
  };
}

export function landingCopyHelperText(state, fallback = "") {
  if (!state) return fallback || "";
  const counter = `${state.length}/${state.max}`;
  if (state.legacyUnchanged) {
    return `${counter} · Este texto supera el nuevo máximo. Se conserva mientras no lo modifiques; si lo editás, reducilo antes de guardar.`;
  }
  if (state.invalid) {
    return `${counter} · Reducí ${state.overBy} ${state.overBy === 1 ? "carácter" : "caracteres"} para guardar.`;
  }
  if (state.length === state.max) return `${counter} · Llegaste al máximo.`;
  return fallback ? `${counter} · ${fallback}` : counter;
}

export function validateLandingCopyDraft(form = {}, baselineForm = {}) {
  const errors = [];
  const legacy = [];
  collectLandingCopyEntries(form).forEach((entry) => {
    const length = landingCopyLength(entry.value);
    if (length <= entry.max) return;
    const historical = historicalLandingCopyValue(form, baselineForm, entry.path);
    const issue = { ...entry, length, overBy: length - entry.max };
    if (sameText(entry.value, historical)) legacy.push(issue);
    else errors.push(issue);
  });
  return { valid: errors.length === 0, errors, legacy };
}

export function copySectionIdForPath(path) {
  const candidate = String(path || "");
  if (candidate.startsWith("navigation.")) return "navbar";
  if (candidate.startsWith("hero.")) return "hero";
  if (candidate.startsWith("catalog.spotlight.")) return "spotlight";
  if (candidate.startsWith("catalog.featured.")) return "featured";
  if (candidate.startsWith("catalog.brands.")) return "brands";
  if (candidate.startsWith("catalog.categories.") || candidate.startsWith("catalog.cards.")) return "categories";
  if (candidate.startsWith("about.")) return "about";
  if (candidate.startsWith("highlights")) return "highlights";
  if (candidate.startsWith("contact.")) return "contact";
  if (candidate.startsWith("footer.") || candidate === "business.shortDescription" || candidate === "navigation.socialTitle") return "footer";
  if (candidate.startsWith("layout.segments.")) return "custom";
  return "";
}
